/**
 * V2 duplicate: creates a fresh broadcast via Audiences API with a cloned frame
 * from Connect API (dragon-tiles), so edits to the copy never bleed into the
 * original broadcast.
 *
 * Not transactional across services — if frame creation succeeds but broadcast
 * creation fails, the frame is deleted as compensation.
 *
 * TODO(connect-api): Replace with a single atomic RPC once broadcasts and
 * frames are unified under connect-api.
 */
import type {
  AudiencesBroadcast,
  CreateBroadcastRequestV2,
} from "@/features/BroadcastV2/types";
import type { CreateFramePayloadParsed } from "@/frames/schemas";
import type { Frame, FrameVariant } from "@/types/dragon-tiles";

import { createBroadcastV2ViaAudiencesApi } from "@/api/broadcastV2Audiences";
import { createFrame, deleteFrame, getFrame } from "@/api/dragon-tiles";
import { smsVariationFrameName } from "@/features/BroadcastV2/store/variationTypes";
import { createFrameVariantPayloadSchema } from "@/frames/schemas";

function cloneFrameMetadata(metadata: Frame["metadata"]): Frame["metadata"] {
  if (!metadata) {
    return undefined;
  }

  return {
    ...metadata,
    audience_ids: metadata.audience_ids
      ? [...metadata.audience_ids]
      : undefined,
    events: metadata.events?.map((eventItem) => ({ ...eventItem })),
  };
}

export function buildCreateFramePayloadFromSourceFrame(
  frame: Frame,
  siteId: string,
  frameName: string,
): CreateFramePayloadParsed {
  const innerTile = frame.inner_tile;
  if (!innerTile) {
    throw new Error("This broadcast has no tile to duplicate.");
  }

  return {
    name: frameName,
    is_active: false,
    site: siteId,
    inner_tile: innerTile,
    metadata: cloneFrameMetadata(frame.metadata),
    frame_variants: frame.frame_variants.map((variant: FrameVariant) =>
      createFrameVariantPayloadSchema.parse({
        content_text: variant.content_text,
        content_url: variant.content_url,
        content_json: variant.content_json,
        content_plaintext: variant.content_plaintext ?? null,
        editor_type: variant.editor_type,
        medium: variant.medium,
        config: variant.config,
      }),
    ),
  };
}

/** Exported for its contract test: a duplicate must come out hand-made
 * (no `source` stamp), even when the original is calendar-owned. */
export function buildCreateBroadcastPayload(
  source: AudiencesBroadcast,
  name: string,
  frameIds: string[],
): CreateBroadcastRequestV2 {
  const sourceVariations = source.variations ?? [];
  return {
    name,
    audienceIds: source.audienceIds ?? [],
    ...(source.excludedAudienceIds?.length
      ? { excludedAudienceIds: source.excludedAudienceIds }
      : {}),
    transactional: source.transactional,
    ...(source.forceChannel === "MEDIUM_TYPE_EMAIL" ||
    source.forceChannel === "MEDIUM_TYPE_SMS"
      ? { forceChannel: source.forceChannel }
      : {}),
    variations: frameIds.map((frameId, index) => ({
      frameId,
      emailOptions: sourceVariations[index]?.emailOptions ?? undefined,
    })),
    ...(source.abTestOption ? { abTestOption: source.abTestOption } : {}),
  };
}

export async function duplicateBroadcastV2(params: {
  siteId: string;
  sourceBroadcast: AudiencesBroadcast;
  name: string;
}): Promise<string> {
  const { siteId, sourceBroadcast } = params;
  const trimmedName = params.name.trim();
  if (!trimmedName) {
    throw new Error("Broadcast name is required.");
  }

  const sourceVariations = sourceBroadcast.variations ?? [];
  if (!sourceVariations.some((variation) => variation.frameId)) {
    throw new Error("This broadcast has no frame to duplicate.");
  }

  // Clone every variation's tile so an A/B broadcast is reproduced faithfully
  // (variation B is otherwise dropped). Frames are cloned sequentially so the
  // cleanup accumulator stays accurate if any step fails midway.
  const createdFrameIds: string[] = [];
  try {
    for (let index = 0; index < sourceVariations.length; index++) {
      const sourceFrameId = sourceVariations[index].frameId;
      const frame = await getFrame(siteId, sourceFrameId);
      const framePayload = buildCreateFramePayloadFromSourceFrame(
        frame,
        siteId,
        smsVariationFrameName(trimmedName, index, sourceVariations.length),
      );
      const createdFrame = await createFrame(siteId, framePayload);
      createdFrameIds.push(createdFrame.id);
    }

    const broadcastPayload = buildCreateBroadcastPayload(
      sourceBroadcast,
      trimmedName,
      createdFrameIds,
    );
    const { id } = await createBroadcastV2ViaAudiencesApi(
      siteId,
      broadcastPayload,
    );
    return id;
  } catch (error) {
    await Promise.all(
      createdFrameIds.map((frameId) =>
        deleteFrame(siteId, frameId).catch(() => {}),
      ),
    );
    throw error;
  }
}
